import { useEffect, useState, Fragment } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import api from "@/config/api";
import { ArrowLeft, Edit, FileText, CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import { validateDueDate } from "@/utils/validation";
import { showError } from "@/utils/dialog";
import { toLocalDateInputValue } from "@/utils/date";

export default function ViewOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [approvalPolicy, setApprovalPolicy] = useState("any_one");
  const [searchParams] = useSearchParams();
  const [expandedServiceRows, setExpandedServiceRows] = useState(new Set());
  const [showServiceCost, setShowServiceCost] = useState(false);
  const [vatInput, setVatInput] = useState("5");
  const [discountInput, setDiscountInput] = useState("0");

  useEffect(() => {
    if (id) {
      api.orders
        .getById(id)
        .then(setOrder)
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
      api.settings
        .getCompany()
        .then((s) =>
          setApprovalPolicy(
            ["none", "any_one", "both"].includes(s?.invoice_approval_policy)
              ? s.invoice_approval_policy
              : "any_one",
          ),
        )
        .catch(() => {});
    }
  }, [id]);

  // If opened with ?generateInvoice=1 and order is open, auto-open close modal
  useEffect(() => {
    if (!order || order.status !== "open") return;
    if (searchParams.get("generateInvoice") === "1") {
      setVatInput("5");
      setDiscountInput("0");
      setDueDate(order.due_date ? order.due_date.slice(0, 10) : "");
      setNotes(order.notes || "");
      setCloseModal(true);
    }
  }, [order, searchParams]);

  const handleCloseOrder = async () => {
    if (dueDate) {
      const dueErr = validateDueDate(dueDate);
      if (dueErr) {
        await showError(dueErr, "Validation Error");
        return;
      }
    }
    const parsedVat =
      vatInput === "" || vatInput == null ? 0 : parseFloat(vatInput);
    const parsedDiscount =
      discountInput === "" || discountInput == null
        ? 0
        : parseFloat(discountInput);
    setClosing(true);
    try {
      const result = await api.orders.close(id, {
        vat_percentage: Number.isFinite(parsedVat) ? parsedVat : 0,
        discount: Number.isFinite(parsedDiscount) ? parsedDiscount : 0,
        due_date: dueDate || undefined,
        notes: notes || undefined,
      });
      setCloseModal(false);
      setOrder((prev) =>
        prev
          ? { ...prev, status: "closed", invoice_id: result.invoice_id }
          : null,
      );
      if (result.invoice_id) {
        navigate(`/invoices/${result.invoice_id}`);
      } else {
        navigate("/orders");
      }
    } catch (err) {
      console.error(err);
      await showError(err.message || "Failed to close order");
    } finally {
      setClosing(false);
    }
  };

  const handleCancelOrder = async () => {
    setCancelling(true);
    try {
      const result = await api.orders.cancel(id, { reason: cancelReason || "" });
      setOrder((prev) =>
        prev ? { ...prev, ...(result?.order || {}), status: "cancelled" } : prev,
      );
      setCancelReason("");
      setCancelModal(false);
    } catch (err) {
      await showError(err.message || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString(undefined, {
      dateStyle: "medium",
    });
  };

  if (loading || !order) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isOpen = order.status === "open";

  const toggleServiceExpand = (index) => {
    setExpandedServiceRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const formatPairs = (obj) => {
    if (!obj || typeof obj !== "object") return [];
    return Object.entries(obj).filter(
      ([, v]) => v != null && String(v).trim() !== "",
    );
  };

  const parseChipValues = (value) => {
    if (value == null) return [];
    if (Array.isArray(value)) {
      return value.map((x) => String(x || "").trim()).filter(Boolean);
    }
    const raw = String(value).trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((x) => String(x || "").trim()).filter(Boolean);
      }
    } catch (_) {
      // Continue fallback parsing.
    }
    if (raw.includes("\n")) {
      return raw.split("\n").map((x) => x.trim()).filter(Boolean);
    }
    if (raw.includes(",")) {
      return raw.split(",").map((x) => x.trim()).filter(Boolean);
    }
    return [];
  };

  const renderFieldValue = (value) => {
    const chips = parseChipValues(value);
    if (chips.length > 0) {
      return (
        <span className="inline-flex flex-wrap gap-1 align-middle">
          {chips.map((chip, idx) => (
            <span
              key={`${chip}-${idx}`}
              className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] text-stone-700"
            >
              {chip}
            </span>
          ))}
        </span>
      );
    }
    return String(value);
  };

  const subtotal =
    order.services?.reduce((s, svc) => {
      const n = parseFloat(svc.amount ?? svc.sale_amount ?? 0);
      if (Number.isFinite(n) && n > 0) return s + n;
      const v = Array.isArray(svc.vendors) ? svc.vendors : [];
      if (v.length === 0) return s;
      return s + v.reduce((a, x) => a + (parseFloat(x.sell_price) || 0), 0);
    }, 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/orders")}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            title="Back to Orders"
          >
            <ArrowLeft size={20} className="text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-secondary">
              Order {order.id.slice(0, 8)}
            </h1>
            <p className="text-stone-600 text-sm">
              {formatDate(order.created_at)} · {order.customer_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              isOpen
                ? "bg-amber-100 text-amber-700"
                : order.status === "cancelled"
                  ? "bg-rose-100 text-rose-700"
                  : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {order.status}
          </span>
          {isOpen && (
            <>
              <Link
                to={`/workflow-tickets?source_type=order&source_id=${encodeURIComponent(id)}&title=${encodeURIComponent(`Order ${order.id.slice(0, 8)} handoff`)}&description=${encodeURIComponent(`Created from order ${order.id.slice(0, 8)} for customer ${order.customer_name || ""}`)}&to_role=finance`}
                className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50"
              >
                Create Ticket
              </Link>
              <button
                onClick={() => navigate(`/orders/${id}/edit`)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 text-stone-700"
              >
                <Edit size={18} />
                Edit
              </button>
              <button
                onClick={() => {
                  setVatInput("5");
                  setDiscountInput("0");
                  setDueDate(order.due_date ? order.due_date.slice(0, 10) : "");
                  setNotes(order.notes || "");
                  setCloseModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                <CheckCircle size={18} />
                Close & Create Invoice
              </button>
              <button
                onClick={() => {
                  setCancelReason(order?.cancelled_reason || "");
                  setCancelModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700"
              >
                Cancel order
              </button>
            </>
          )}
          {!isOpen && order.invoice_id && (
            <Link
              to={`/invoices/${order.invoice_id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              <FileText size={18} />
              View Invoice
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-4">
            Customer
          </h2>
          <p className="font-medium text-stone-800">{order.customer_name}</p>
          {order.customer_email && (
            <p className="text-stone-600 text-sm">{order.customer_email}</p>
          )}
          {order.customer_phone && (
            <p className="text-stone-600 text-sm">{order.customer_phone}</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-4">Payment</h2>
          <p className="text-stone-700">
            Type:{" "}
            <span className="capitalize">
              {(order.payment_type || "—").replace("_", " ")}
            </span>
          </p>
          {order.payment_details &&
            typeof order.payment_details === "object" &&
            Object.keys(order.payment_details).length > 0 && (
              <div className="mt-2 text-sm text-stone-600 space-y-1">
                {Object.entries(order.payment_details).map(([k, v]) =>
                  v ? (
                    <p key={k}>
                      {k.replace(/_/g, " ")}: {String(v)}
                    </p>
                  ) : null,
                )}
              </div>
            )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="p-6 pb-0 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-secondary">Services</h2>
          <label className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer shrink-0">
            <input
              type="checkbox"
              className="rounded border-stone-300"
              checked={showServiceCost}
              onChange={(e) => setShowServiceCost(e.target.checked)}
            />
            Show cost (buy) prices
          </label>
        </div>
        <p className="px-6 pt-1 text-sm text-stone-500">
          Use the arrow to expand a line for shared fields, passengers, and each vendor (buy / sell).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-y border-stone-200">
              <tr>
                <th className="w-10 py-3 px-2 text-sm font-medium text-stone-600" aria-label="Expand" />
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">
                  Type / Name
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600 min-w-[10rem]">
                  Vendor
                </th>
                {showServiceCost && (
                  <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">
                    Cost
                  </th>
                )}
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">
                  Sale
                </th>
                <th className="text-center py-3 px-4 text-sm font-medium text-stone-600">
                  VAT
                </th>
              </tr>
            </thead>
            <tbody>
              {order.services?.map((svc, idx) => {
                const isOpen = expandedServiceRows.has(idx);
                const vList = Array.isArray(svc.vendors) ? svc.vendors : [];
                const costTotal =
                  svc.cost != null
                    ? parseFloat(svc.cost)
                    : vList.length > 0
                      ? vList.reduce((a, v) => a + (parseFloat(v.buy_price) || 0), 0)
                      : 0;
                const sellTotal =
                  parseFloat(svc.amount ?? svc.sale_amount ?? 0) ||
                  (vList.length > 0
                    ? vList.reduce((a, v) => a + (parseFloat(v.sell_price) || 0), 0)
                    : 0);
                const vendorSummary =
                  vList.length > 0
                    ? vList
                        .map((v) => v.vendor_name)
                        .filter(Boolean)
                        .join(" · ") || `${vList.length} vendors`
                    : svc.vendor_name || "—";
                const colCount =
                  1 +
                  1 +
                  1 +
                  (showServiceCost ? 1 : 0) +
                  1 +
                  1;
                return (
                  <Fragment key={svc.id}>
                    <tr
                      className={`border-b border-stone-100 align-top ${
                        idx % 2 === 0 ? "bg-stone-50/50 hover:bg-stone-100/50" : "hover:bg-stone-50/80"
                      }`}
                    >
                      <td className="py-3 px-2 text-center">
                        <button
                          type="button"
                          onClick={() => toggleServiceExpand(idx)}
                          className="p-1.5 text-stone-600 hover:bg-stone-200/80 rounded-lg"
                          title={isOpen ? "Collapse" : "Expand details"}
                        >
                          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-secondary">
                          {svc.name}
                        </span>
                        {svc.service_type && (
                          <span className="ml-2 text-xs text-stone-500 capitalize">
                            ({String(svc.service_type).replace(/_/g, " ")})
                          </span>
                        )}
                        {svc.notes && (
                          <p className="mt-1 text-xs text-stone-500 line-clamp-2">
                            {svc.notes}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-stone-600 text-sm break-words max-w-[16rem]">
                        {vendorSummary}
                        {vList.length > 1 && (
                          <span className="ml-1 text-xs text-stone-500">({vList.length} lines)</span>
                        )}
                      </td>
                      {showServiceCost && (
                        <td className="py-3 px-4 text-right font-mono text-stone-700 text-sm">
                          {order.currency || "OMR"}{" "}
                          {Number.isFinite(costTotal) ? costTotal.toFixed(2) : (parseFloat(svc.cost) || 0).toFixed(2)}
                        </td>
                      )}
                      <td className="py-3 px-4 text-right font-mono text-stone-800 text-sm">
                        {order.currency || "OMR"}{" "}
                        {sellTotal.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {svc.vat_applicable ? (
                          <span className="text-emerald-600">Yes</span>
                        ) : (
                          <span className="text-stone-400">No</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${svc.id}-detail`} className="bg-stone-50/90 border-b border-stone-100">
                        <td colSpan={colCount} className="px-4 py-4 text-sm text-stone-700">
                          <div className="space-y-3 text-xs sm:text-sm">
                            <div className="flex flex-wrap gap-4">
                              <div>
                                <p className="font-semibold text-stone-800">Service</p>
                                <p>Type: {svc.service_type ? String(svc.service_type).replace(/_/g, " ") : "—"}</p>
                                {svc.payment_type && (
                                  <p>Payment type: {String(svc.payment_type).replace(/_/g, " ")}</p>
                                )}
                              </div>
                              {Array.isArray(svc.passenger_assignments) && svc.passenger_assignments.length > 0 && (
                                <div>
                                  <p className="font-semibold text-stone-800">Passengers</p>
                                  <ul className="list-disc list-inside space-y-0.5">
                                    {svc.passenger_assignments.map((pax) => (
                                      <li key={pax.passenger_id}>
                                        {pax.name || pax.full_name || pax.passenger_id}
                                        {pax.role && ` (${pax.role})`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                            {formatPairs(svc.extra_fields).length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                                  Line details (shared)
                                </p>
                                <ul className="space-y-1 list-disc pl-5 text-stone-700">
                                  {formatPairs(svc.extra_fields).map(([k, v]) => (
                                    <li key={k}>
                                      <span className="text-stone-500">
                                        {k.replace(/_/g, " ")}:{" "}
                                      </span>
                                      {renderFieldValue(v)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {vList.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                                  Vendors (lines)
                                </p>
                                {vList.map((v, vi) => (
                                  <div
                                    key={v.vendor_id || `${vi}-${v.vendor_name || "v"}`}
                                    className="rounded-lg border border-stone-200 bg-white px-3 py-2"
                                  >
                                    <p className="font-medium text-stone-800">
                                      {v.vendor_name || "Vendor"}{" "}
                                      {v.vendor_id && (
                                        <span className="text-xs font-mono text-stone-500">
                                          ({String(v.vendor_id).slice(0, 8)}…)
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-stone-600 mt-1">
                                      Buy: {order.currency || "OMR"}{" "}
                                      {(parseFloat(v.buy_price) || 0).toFixed(2)} — Sell: {order.currency || "OMR"}{" "}
                                      {(parseFloat(v.sell_price) || 0).toFixed(2)}
                                    </p>
                                    {v.extra_fields && formatPairs(v.extra_fields).length > 0 && (
                                      <ul className="mt-2 space-y-0.5 pl-3 list-disc text-stone-600">
                                        {formatPairs(v.extra_fields).map(([k, val]) => (
                                          <li key={k}>
                                            <span className="text-stone-500">
                                              {k.replace(/_/g, " ")}:{" "}
                                            </span>
                                            {renderFieldValue(val)}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {vList.length === 0 && (svc.vendor_name || svc.vendor_id) && (
                              <p className="text-stone-600">
                                <span className="font-medium">Legacy vendor: </span>
                                {svc.vendor_name || svc.vendor_id}
                              </p>
                            )}
                            {Array.isArray(svc.service_entities) && svc.service_entities.length > 0 && (
                              <div>
                                <p className="font-semibold text-stone-800 mb-1">Linked services / segments</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                  {svc.service_entities.map((ent) => (
                                    <li key={ent.id}>{ent.name || ent.description || ent.id}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-4 bg-stone-50 border-t border-stone-200 flex justify-end">
          <span className="font-semibold text-secondary">
            Subtotal: {order.currency || "OMR"} {subtotal.toFixed(2)}
          </span>
        </div>
      </div>

      {order.notes && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-2">Notes</h2>
          <p className="text-stone-600 whitespace-pre-wrap">{order.notes}</p>
        </div>
      )}
      {order.status === "cancelled" && (
        <div className="bg-white rounded-xl border border-rose-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-rose-700 mb-2">
            Cancellation history
          </h2>
          <div className="space-y-1 text-sm text-stone-700">
            <p>
              <span className="font-medium">Cancelled on:</span>{" "}
              {order.cancelled_at ? formatDate(order.cancelled_at) : "—"}
            </p>
            <p>
              <span className="font-medium">Cancelled by:</span>{" "}
              {order.cancelled_by_name || "—"}
            </p>
            <p className="whitespace-pre-wrap">
              <span className="font-medium">Reason:</span>{" "}
              {order.cancelled_reason || "No reason provided"}
            </p>
          </div>
        </div>
      )}

      {closeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-secondary mb-4">
              Close order & create invoice
            </h3>
            <p className="text-stone-600 text-sm mb-4">
              Set VAT and discount for the generated invoice. Subtotal from
              order: {order.currency} {subtotal.toFixed(2)}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-stone-700 mb-1">
                  VAT %
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={vatInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setVatInput(value);
                  }}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-stone-700 mb-1">
                  Discount ({order.currency})
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discountInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDiscountInput(value);
                  }}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-stone-700 mb-1">
                  Payment due date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={toLocalDateInputValue()}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-stone-700 mb-2">
                  Approvals
                </label>
                <div className="space-y-2 rounded-lg border border-stone-200 px-3 py-2">
                  <p className="text-xs text-stone-500">
                    Policy:{" "}
                    {approvalPolicy === "none"
                      ? "None"
                      : approvalPolicy === "both"
                        ? "Both required"
                        : "Any one required"}
                  </p>
                  <p className="text-xs text-stone-600">
                    {approvalPolicy === "none"
                      ? "Invoice will be approved immediately after creation."
                      : approvalPolicy === "any_one"
                        ? "System will create approval tickets for Management and Finance. Any one approved ticket will approve the invoice."
                        : "System will create approval tickets for Management and Finance. Both tickets must be completed to approve the invoice."}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm text-stone-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button
                type="button"
                onClick={() => setCloseModal(false)}
                className="px-4 py-2 border border-stone-300 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseOrder}
                disabled={closing}
                className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
              >
                {closing ? "Creating..." : "Create Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
      {cancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-secondary mb-3">
              Cancel order
            </h3>
            <p className="text-sm text-stone-600 mb-4">
              This marks the order as <strong>cancelled (lost)</strong> and blocks invoice conversion.
            </p>
            <label className="block text-sm text-stone-700 mb-1">
              Cancel reason (optional)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg"
              placeholder="Why this order was cancelled..."
            />
            <div className="flex gap-2 justify-end mt-6">
              <button
                type="button"
                onClick={() => setCancelModal(false)}
                disabled={cancelling}
                className="px-4 py-2 border border-stone-300 rounded-lg"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleCancelOrder}
                disabled={cancelling}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg disabled:opacity-50"
              >
                {cancelling ? "Cancelling..." : "Confirm cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
